import { Link } from 'react-router-dom';
import TopBar from '@/components/feature/TopBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation, useStaggeredAnimation, useParallax } from '@/hooks/useScrollAnimation';

const values = [
  { icon: 'ri-heart-3-line', title: 'Compromiso', desc: 'Cuidamos a cada paciente con la dedicación que brindaríamos a un familiar.' },
  { icon: 'ri-shield-check-line', title: 'Excelencia', desc: 'Los más altos estándares en cada diagnóstico, tratamiento y atención.' },
  { icon: 'ri-hand-heart-line', title: 'Calidez Humana', desc: 'La empatía y el respeto son pilares tan fundamentales como la ciencia.' },
  { icon: 'ri-lightbulb-line', title: 'Innovación', desc: 'Tecnología moderna y actualización continua para diagnósticos precisos.' },
];

const teamPhotos = [
  { src: 'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/8c7531a9265dd00c669c1a88d5d46985.png', alt: 'Bendición de la clínica', caption: 'Bendición de nuestras instalaciones', tall: true },
  { src: 'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/47de8ec63d5e83e8074966e28a6a59cd.png', alt: 'Brindis de accionistas y médicos', caption: 'Nuestros accionistas y directivos', tall: false },
  { src: 'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/751a4ba43261092a8c20ee9dd233057a.png', alt: 'Plantel médico y de enfermería', caption: 'Plantel médico y de enfermería', tall: false },
  { src: 'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/f53a02f12d709b868683438b16a57827.png', alt: 'Plantel de enfermería', caption: 'Equipo de enfermería', tall: false },
  { src: 'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/5ffc415b0c6b5998bcc18c756456648b.png', alt: 'Fundadores de Vitalmedic', caption: 'Fundadores de Vitalmedic', tall: true },
  { src: 'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/b8d8b1a3376b3130d31c95578000989a.png', alt: 'Equipo en inauguración', caption: 'Inauguración de la clínica', tall: false },
];

export default function AboutPage() {
  const heroAnim = useScrollAnimation();
  const missionAnim = useScrollAnimation();
  const valuesAnim = useStaggeredAnimation();
  const teamAnim = useStaggeredAnimation();
  const missionImg = useParallax(-0.09);

  return (
    <>
      <TopBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section ref={heroAnim.ref} className="relative overflow-hidden bg-background-100 py-20 md:py-28 px-4 md:px-8 lg:px-12">
          <div className="absolute inset-0 texture-grid opacity-60" />
          <div className="absolute -top-16 -right-16 w-96 h-96 bg-primary-100/50 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-accent-200/40 rounded-full blur-3xl" />
          <div className="relative max-w-4xl mx-auto text-center">
            <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50 text-primary-600 text-xs font-bold uppercase tracking-[0.15em] mb-6 border border-primary-200 reveal ${heroAnim.isVisible ? 'is-in' : ''}`}>
              <span className="w-2 h-2 rounded-full bg-accent-500" />
              Sobre Nosotros
            </span>
            <h1 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-foreground-950 mb-6 leading-[1.05] reveal ${heroAnim.isVisible ? 'is-in' : ''}`}>
              Historia, misión y <span className="italic text-primary-600">valores</span>
            </h1>
            <p className={`text-foreground-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed reveal ${heroAnim.isVisible ? 'is-in' : ''}`}>
              Conoce quiénes somos, qué nos motiva y por qué miles de familias en Tarija confían en nosotros.
            </p>
          </div>
        </section>

        {/* Mission / Vision */}
        <section className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background-50 relative overflow-hidden">
          <div className="max-w-7xl mx-auto" ref={missionAnim.ref}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              <div className={`lg:col-span-5 relative reveal-left ${missionAnim.isVisible ? 'is-in' : ''}`}>
                <div className="absolute -top-5 -right-5 bottom-10 left-8 rounded-[2.5rem] bg-accent-400/25 -z-10" />
                <div
                  ref={missionImg.ref}
                  className="rounded-[2.5rem] overflow-hidden border-4 border-background-50"
                  style={{ transform: `translateY(${missionImg.offset}px)` }}
                >
                  <img
                    src="https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/5ffc415b0c6b5998bcc18c756456648b.png"
                    alt="Fundadores de Clínica Tarija Vitalmedic SRL"
                    className="w-full h-[340px] md:h-[480px] object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-6 -left-3 md:-left-6 bg-secondary-950 rounded-2xl px-6 py-5">
                  <p className="font-heading text-3xl font-bold text-accent-500 leading-none">+8</p>
                  <p className="text-xs text-background-300 mt-1.5">Años de servicio</p>
                </div>
              </div>

              <div className={`lg:col-span-7 reveal-right ${missionAnim.isVisible ? 'is-in' : ''}`}>
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                      <i className="ri-focus-3-line text-base" />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] text-primary-500 uppercase">Nuestra Misión</span>
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3 leading-tight">
                    Brindar salud con calidad y calidez
                  </h2>
                  <p className="text-foreground-600 leading-relaxed">
                    Ser el centro médico de referencia en Tarija, reconocido por nuestra excelencia clínica, atención personalizada y compromiso con la comunidad. Cada paciente se siente cuidado, escuchado y tratado con el máximo profesionalismo.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500 text-secondary-950">
                      <i className="ri-eye-line text-base" />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] text-accent-700 uppercase">Nuestra Visión</span>
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3 leading-tight">
                    Líderes en salud para toda la región
                  </h2>
                  <p className="text-foreground-600 leading-relaxed">
                    Consolidarnos como la clínica de mayor confianza en el sur de Bolivia, expandiendo nuestros servicios y manteniendo siempre los más altos estándares de calidad, ética y humanidad.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values — dark espresso */}
        <section ref={valuesAnim.ref} className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-secondary-950 relative overflow-hidden">
          <div className="absolute inset-0 texture-dots-light opacity-25" />
          <div className="absolute -top-20 left-10 w-72 h-72 rounded-full bg-primary-600/20 blur-3xl" />
          <div className="relative max-w-7xl mx-auto">
            <div className="max-w-2xl mb-12 md:mb-16">
              <span className={`inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-accent-500 uppercase mb-4 reveal ${valuesAnim.isVisible ? 'is-in' : ''}`}>
                <span className="w-8 h-px bg-accent-500" />
                Nuestros Valores
              </span>
              <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-background-50 leading-[1.08] reveal ${valuesAnim.isVisible ? 'is-in' : ''}`}>
                Los pilares que nos <span className="italic text-accent-500">guían</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {values.map((v, i) => (
                <div
                  key={v.title}
                  className={`rounded-3xl bg-background-50/[0.04] border border-background-50/10 p-6 md:p-8 hover:bg-background-50/[0.07] transition-colors duration-500 reveal ${valuesAnim.isVisible ? 'is-in' : ''} ${i % 2 === 1 ? 'lg:mt-8' : ''}`}
                  style={{ transitionDelay: valuesAnim.getDelay(i) }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 mb-5">
                    <i className={`${v.icon} text-2xl`} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-background-50 mb-2">{v.title}</h3>
                  <p className="text-sm text-background-300 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Gallery — masonry */}
        <section ref={teamAnim.ref} className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
              <span className={`inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-primary-500 uppercase mb-4 reveal ${teamAnim.isVisible ? 'is-in' : ''}`}>
                Nuestro Equipo
              </span>
              <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-950 mb-4 leading-[1.08] reveal ${teamAnim.isVisible ? 'is-in' : ''}`}>
                Las personas detrás de <span className="italic text-primary-600">Vitalmedic</span>
              </h2>
              <p className={`text-foreground-600 text-base leading-relaxed reveal ${teamAnim.isVisible ? 'is-in' : ''}`}>
                Médicos, enfermeras, accionistas y personal que hacen posible nuestra labor.
              </p>
            </div>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
              {teamPhotos.map((photo, i) => (
                <div
                  key={i}
                  className={`group relative rounded-2xl overflow-hidden break-inside-avoid reveal-scale ${teamAnim.isVisible ? 'is-in' : ''}`}
                  style={{ transitionDelay: teamAnim.getDelay(i) }}
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className={`w-full object-cover object-top transition-transform duration-[1.1s] group-hover:scale-105 ${photo.tall ? 'h-[360px] md:h-[420px]' : 'h-[240px] md:h-[280px]'}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-background-50 text-sm font-medium">{photo.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground-950 mb-4">
              ¿Listo para cuidar tu salud?
            </h2>
            <p className="text-foreground-600 mb-8">
              Agenda tu consulta hoy mismo y descubre por qué miles de familias confían en Vitalmedic.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/contacto"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Solicitar Turno
                <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-background-300 text-foreground-800 text-sm font-semibold hover:border-primary-400 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Ver servicios
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}